import { useQuery } from '@apollo/client';
import { createError } from '@common/neo.tax';

import {
  advancedFilter,
  Conditions,
  FilterField,
  GET_SRED_PAYROLL_EXPENSES_SUMMARY,
  GetSredPayrollExpensesSummaryQuery,
  GetSredPayrollExpensesSummaryQueryVariables,
  keywordFilter,
  SredPayrollExpensesSummaryFilter,
  useSticky,
} from '@neo.tax/shared';

export type SredPayrollExpensesSummary = NonNullable<
  GetSredPayrollExpensesSummaryQuery['getSredPayrollExpensesSummary']
>;

type UseSredPayrollExpensesSummaryProps = {
  sredTaxCreditId: string;
  filterConditions?: Conditions<FilterField<SredPayrollExpensesSummaryFilter>>;
  searchKeyword?: string | null;
};

type UseSredPayrollExpensesSummaryResult =
  | {
      loading: true;
      sredPayrollExpensesSummary: null;
    }
  | {
      loading: false;
      sredPayrollExpensesSummary: SredPayrollExpensesSummary;
    };

export const useSredPayrollExpensesSummary = ({
  filterConditions,
  searchKeyword,
  sredTaxCreditId,
}: UseSredPayrollExpensesSummaryProps): UseSredPayrollExpensesSummaryResult => {
  const {
    data: requestData,
    error,
    loading,
  } = useQuery<GetSredPayrollExpensesSummaryQuery, GetSredPayrollExpensesSummaryQueryVariables>(
    GET_SRED_PAYROLL_EXPENSES_SUMMARY,
    {
      variables: {
        sredTaxCreditId,
        filter: {
          and: [
            keywordFilter(['name', 'title'], searchKeyword),
            advancedFilter<SredPayrollExpensesSummaryFilter>(filterConditions),
          ],
        },
      },
    },
  );

  // Keep existing data on the screen while a new query is running, so it
  // doesn't keep clearing the screen as you type into the filter.
  const stickyData = useSticky(requestData);
  const sredPayrollExpensesSummary = stickyData?.getSredPayrollExpensesSummary ?? null;

  if (loading) {
    return {
      loading: true,
      sredPayrollExpensesSummary: null,
    };
  }
  if (error) {
    throw error;
  }

  if (!stickyData || !sredPayrollExpensesSummary) {
    throw createError('MissingSredPayrollExpensesSummary', 'No data returned from getSredPayrollExpensesSummary query');
  }

  return {
    loading,
    sredPayrollExpensesSummary,
  };
};
